import analytics from '@/util/analytics';
import type { ProductDetailData } from '@/types/Product';
import {
  trackPriveePdpAddToClosetClicked,
  trackPriveePdpModalCtaClicked,
  trackPriveePdpAccordion,
  trackPriveePdpUserState,
  type PriveeAddToClosetVariant,
  type PriveeModalCta,
  type PriveeAccordionSection,
  type PriveeAccordionAction,
  type PriveePdpUserState
} from '@/util/privee_analytics';

export interface AddToClosetClickAnalyticsProps {
  parent_sku: string;
  parent_sku_tier: string;
}

/**
 * Segment maps this spec event to Meta's `ViewContent`, turning `product_id`
 * into `content_ids`. Our custom PDP events can't do that, hence 0% catalog
 * match rate.
 *
 * `product_id` must match the catalog Content ID, which the Google Shopping
 * sheet builds as `{sku}_{tier}`. Nothing links the two, so a format change
 * there silently stops matching here.
 */
export function trackProductViewed(product: ProductDetailData): void {
  analytics.track('Product Viewed', {
    product_id: `${product.sku}_${product.productTier}`,
    sku: product.sku,
    name: product.productName,
    brand: product.brand
  });

  // window.braze, not an import: new imports here hit the Next 13 chunk bug.
  // On a direct landing Braze is still loading, so BrazeInit sends it later.
  const lastProductViewed = `${product.brand} ${product.productName}`;
  if (window.braze?.isInitialized()) {
    window.braze
      .getUser()
      ?.setCustomUserAttribute('last_product_viewed', lastProductViewed);
  } else {
    window.brazePendingProductViewed = lastProductViewed;
  }
}

/**
 * Fires the appropriate "add to closet" click analytics event.
 * For Privée products, fires the typed Privée variant. Otherwise, fires the
 * legacy `PDP Add to Closet - <state>` event. The two branches are mutually
 * exclusive — exactly one event per click.
 */
export function trackAddToClosetClick(
  state: PriveeAddToClosetVariant,
  isPriveeProduct: boolean,
  props: AddToClosetClickAnalyticsProps
): void {
  if (isPriveeProduct) {
    trackPriveePdpAddToClosetClicked(state, props);
    return;
  }
  analytics.track(`PDP Add to Closet - ${state}`, props);
}

/**
 * Track a PDP modal CTA click. When isPriveeProduct is true, fires the typed
 * Privée event; otherwise fires the legacy regular PDP event. Mutually exclusive.
 */
export function trackModalCtaClick(
  cta: PriveeModalCta,
  isPriveeProduct: boolean,
  props: AddToClosetClickAnalyticsProps
): void {
  if (isPriveeProduct) {
    trackPriveePdpModalCtaClicked(cta, props);
    return;
  }
  // Legacy non-Privée behavior: only Upgrade and Go to My Account exist.
  // Return state historically fired Go to My Account — preserve that.
  if (cta === 'Return an Item') {
    analytics.track('PDP Modal CTA Clicked - Go to My Account', props);
    return;
  }
  analytics.track(`PDP Modal CTA Clicked - ${cta}`, props);
}

/**
 * Track a PDP accordion section expand/collapse. When isPriveeProduct is true,
 * fires the typed Privée event; otherwise fires the legacy regular PDP event.
 * Mutually exclusive.
 *
 * Note: the legacy expand event uses property name `parentSku` (no underscore)
 * — that is a pre-existing bug in the regular PDP code that we are deliberately
 * NOT fixing here. The Privée event correctly uses `parent_sku`.
 */
export function trackAccordionAction(
  section: PriveeAccordionSection,
  action: PriveeAccordionAction,
  isPriveeProduct: boolean,
  props: { parent_sku: string; parent_sku_tier: string }
): void {
  if (isPriveeProduct) {
    trackPriveePdpAccordion(section, action, props);
    return;
  }
  if (action === 'Collapsed') {
    analytics.track(`PDP ${section} - Collapsed`, {
      parent_sku: props.parent_sku
    });
    return;
  }
  // Legacy Expand event uses parentSku (no underscore) — preserved intentionally
  analytics.track(`PDP ${section} - Expanded`, { parentSku: props.parent_sku });
}

const PRIVEE_USER_STATES: readonly PriveePdpUserState[] = [
  'Available',
  'Too Early',
  'Too Early or Upgrade',
  'Upgrade or Return',
  'Return',
  'Resubscribe',
  'Reapproval',
  'Payment Needed',
  'Generic',
  'Item Unavailable'
] as const;

interface UserStateProps {
  parent_sku: string;
  parent_sku_tier: string;
  add_to_closet_state: 'enabled' | 'disabled';
  cta_subtext?: string;
}

interface LegacyUserStateProps {
  add_to_closet_state: boolean;
  ctaSubText: string;
  parent_sku: string;
  parent_sku_tier: string;
}

/**
 * Track the PDP user state on mount. When isPriveeProduct is true, fires the
 * typed Privée event ONLY for states in the Privée spec whitelist; for unspec'd
 * states (e.g. 'Upgrade', 'Not Approved', 'Logged Out') no Privée event fires.
 * When isPriveeProduct is false, fires the legacy regular PDP event with the
 * legacy property shape. Mutually exclusive.
 */
export function trackPdpUserState(
  state: string,
  isPriveeProduct: boolean,
  legacyProps: LegacyUserStateProps
): void {
  if (isPriveeProduct) {
    if (!PRIVEE_USER_STATES.includes(state as PriveePdpUserState)) {
      return;
    }
    const priveeProps: UserStateProps = {
      parent_sku: legacyProps.parent_sku,
      parent_sku_tier: legacyProps.parent_sku_tier,
      add_to_closet_state: legacyProps.add_to_closet_state
        ? 'enabled'
        : 'disabled',
      ...(legacyProps.ctaSubText ? { cta_subtext: legacyProps.ctaSubText } : {})
    };
    trackPriveePdpUserState(state as PriveePdpUserState, priveeProps);
    return;
  }
  analytics.track(`PDP User State - ${state}`, legacyProps);
}
